import moment from "moment"

const NON_PAYING_PLAN_CODES = ["DEMO"]

/** Aligné sur Establishment::NON_PREMIUM_PLAN_CODES (backend). */
const NON_PREMIUM_PLAN_CODES = ["FREEMIUM", "FREEMIUM_TRIAL", "DEMO"]

export function isNonPayingPlanCode(planCode: string | null | undefined): boolean {
  if (!planCode) {
    return true
  }

  return NON_PAYING_PLAN_CODES.includes(planCode.toUpperCase())
}

export { NON_PAYING_PLAN_CODES }
type ContractLike = Record<string, any>

export function getPlanCode(contract: ContractLike | null | undefined): string {
  const plan = contract?.product_plan ?? contract?.productPlan
  const code = (plan as { code?: string } | undefined)?.code
  return code ? code.toUpperCase() : ""
}

export function isContractInValidPeriod(contract: ContractLike): boolean {
  const now = moment()
  const validFrom = contract.valid_from ? moment(String(contract.valid_from)) : null
  const validThrough = contract.valid_through ? moment(String(contract.valid_through)) : null

  const hasStarted = !validFrom || (validFrom.isValid() && now.isSameOrAfter(validFrom, "day"))
  const notExpired = !validThrough || (validThrough.isValid() && now.isSameOrBefore(validThrough, "day"))

  return hasStarted && notExpired
}

/**
 * Contrat à utiliser pour les droits d'accès applicatifs.
 * L'API ne met pas un contrat résilié dans current_contract ; tant que sa date de fin
 * n'est pas dépassée, il doit pourtant continuer à ouvrir les modules souscrits.
 */
export function getAccessContract(establishment: ContractLike | null | undefined): ContractLike | null {
  const currentContract = establishment?.current_contract as ContractLike | null | undefined
  const lastTerminatedContract = establishment?.last_terminated_contract as ContractLike | null | undefined
  const currentValid = currentContract ? isContractInValidPeriod(currentContract) : false
  const lastTerminatedValid = lastTerminatedContract ? isContractInValidPeriod(lastTerminatedContract) : false
  const selectedContract = currentValid ? currentContract : lastTerminatedValid ? lastTerminatedContract : null

  return selectedContract
}

export function getAccessPlanCode(establishment: ContractLike | null | undefined): string {
  return getPlanCode(getAccessContract(establishment))
}

/**
 * Indique si l'établissement a déjà un contrat payant en cours (hors DEMO).
 */
/**
 * Établissement avec contrat payant actif (assistant premium, pas l'assistant local).
 * Indépendant du flag compte `has_premium` qui inclut aussi DEMO.
 */
export function isPremiumEstablishment(establishment: ContractLike | null | undefined): boolean {
  const planCode = getAccessPlanCode(establishment)
  if (!planCode || NON_PREMIUM_PLAN_CODES.includes(planCode)) {
    return false
  }

  const contract = getAccessContract(establishment)
  return contract ? isContractInValidPeriod(contract) : false
}

export function hasActivePayingContract(establishment: ContractLike | null | undefined): boolean {
  const contract = getAccessContract(establishment)
  if (!contract) {
    return false
  }

  const planCode = getPlanCode(contract)
  if (!planCode || isNonPayingPlanCode(planCode)) {
    return false
  }

  return true
}

export function hasAnyPaidEstablishment(establishments: ContractLike[] | null | undefined): boolean {
  return (establishments ?? []).some((establishment) => hasActivePayingContract(establishment))
}

export function getActivePayingContractLabel(establishment: ContractLike | null | undefined): string {
  const contract = getAccessContract(establishment)
  if (!contract) {
    return ""
  }

  const plan = contract.product_plan ?? contract.productPlan
  return (
    (plan as { name?: string; code?: string } | undefined)?.name ||
    (plan as { name?: string; code?: string } | undefined)?.code ||
    ""
  )
}

export function getShootingMonthFromContract(contract: ContractLike | null | undefined): string | null {
  const shootingDate = contract?.shooting_date ?? contract?.shootingDate
  if (!shootingDate) {
    return null
  }

  const match = String(shootingDate).match(/^(\d{4}-\d{2})/)
  return match ? match[1] : null
}

/**
 * Résout le mois de tournage (YYYY-MM) du contrat upsellé.
 * Cherche d'abord sur le contrat ciblé, puis sur les autres contrats de la souscription courante.
 */
export function resolveUpsellShootingMonth(
  establishment: ContractLike | null | undefined,
  contractId?: string | number | null,
): string | null {
  if (!establishment) {
    return null
  }

  const contracts: ContractLike[] = []
  const pushContract = (contract: ContractLike | null | undefined) => {
    if (!contract) {
      return
    }

    const alreadyAdded = contracts.some((existing) => Number(existing.id) === Number(contract.id))
    if (!alreadyAdded) {
      contracts.push(contract)
    }
  }

  pushContract(establishment.current_contract as ContractLike | null | undefined)
  pushContract(establishment.last_terminated_contract as ContractLike | null | undefined)

  const subscriptionContracts = (establishment.current_subscription as { contracts?: ContractLike[] } | undefined)?.contracts
  if (Array.isArray(subscriptionContracts)) {
    subscriptionContracts.forEach((contract) => pushContract(contract))
  }

  if (contractId != null) {
    const targetId = Number(contractId)
    const matchedContract = contracts.find((contract) => Number(contract.id) === targetId)
    const shootingMonth = getShootingMonthFromContract(matchedContract)
    if (shootingMonth) {
      return shootingMonth
    }
  }

  for (const contract of contracts) {
    const shootingMonth = getShootingMonthFromContract(contract)
    if (shootingMonth) {
      return shootingMonth
    }
  }

  return null
}

export function resolveShootingMonthFromSubscriptions(
  subscriptions: any[],
  establishmentId?: string | number | null,
  contractId?: string | number | null,
): string | null {
  if (!Array.isArray(subscriptions)) {
    return null
  }

  for (const subscription of subscriptions) {
    const contracts = Array.isArray(subscription?.contracts) ? subscription.contracts : []

    if (contractId != null) {
      const matchedContract = contracts.find((contract) => Number(contract?.id) === Number(contractId))
      const shootingMonth = getShootingMonthFromContract(matchedContract)
      if (shootingMonth) {
        return shootingMonth
      }
    }

    if (establishmentId != null) {
      for (const contract of contracts) {
        if (Number(contract?.establishment_id) !== Number(establishmentId)) {
          continue
        }

        const shootingMonth = getShootingMonthFromContract(contract)
        if (shootingMonth) {
          return shootingMonth
        }
      }
    }
  }

  return null
}
